import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MyPlanStoryService } from '../../../services/my-plan-story/my-plan-story.service';
import { DataService } from '../../../services/data/data.service';
import { Ga4Service } from '../../../services/ga4/ga4.service';
import { DASHBOARD_PAGE_LOCATION, ERR_EVENT, MY_PLANS_PAGE, MY_PLAN_STORY_LOCATION, RE_PLAN_RENEWAL_BUTTON, SR_LIST_PAGE, getReRenewalAiMessageEvent, getReRenewalButtonEvent } from '../../../services/ga4/ga4.constant';
import { IApiClaimsHistory, IContractData } from '../../../interfaces/IJob';
import { IMyPlanStory, MyPlanStoryScenario } from '../../../interfaces/IMyPlanStory';
import { IAlertCardContent } from '../../../interfaces/IAlertCard';
import { resolveMyPlanStoryScenario } from '../../../utils/plan-context.util';
import { PAGE_SOURCE } from '../../../utils/constants';

@Component({
  selector: 'app-my-plan-story',
  templateUrl: './my-plan-story.component.html',
  styleUrl: './my-plan-story.component.scss',
})
export class MyPlanStoryComponent implements OnChanges, OnDestroy {
  @Input() contractData: IContractData | null = null;
  @Input() jobsData: IApiClaimsHistory[] = [];
  // True once the parent's Jobs API call has actually settled for the current
  // contract. Required because an empty jobsData array is ambiguous — it means
  // both "not fetched yet" and "fetched, zero claims" — so length alone can't
  // tell us it's safe to generate the story exactly once.
  @Input() jobsDataLoaded = false;
  // The dashboard's OWN, already-computed "come back" eligibility signal —
  // passed straight through rather than recomputed here, purely so a
  // comeback-eligible member (lapsed/cancelled plan) never sees this renewal
  // card even in the rare case both conditions were somehow true at once.
  // PHASE 1 only renders the renewal scenario (see ngOnChanges), so this is
  // used solely for that precedence check, not to render a comeback narrative.
  @Input() showComeBackAlert = false; // dashboard.component.ts: showToastForPlanStatus
  // Which page hosts this card (PAGE_SOURCE.DASHBOARD / MY_PLAN / SERVICE_REQUESTS),
  // threaded into getRenewalBillingAlert() so alertCardContent.pageSource -
  // and therefore GA4/navigation attribution - reflects the actual host page
  // instead of always reading as the dashboard.
  @Input() pageSource: string = PAGE_SOURCE.DASHBOARD;

  // Emits true when the backend narrative call fails, so the dashboard can fall
  // back to the original static renewal alert card instead of showing this card
  // in a broken/empty state (see loadStory) — emits false again once a load
  // for a new signature succeeds.
  @Output() storyFailed = new EventEmitter<boolean>();

  isLoading = false;
  story: IMyPlanStory | null = null;
  // True when the last narrative load failed — hides the whole card (see
  // template) rather than showing a default/fallback narration, per product
  // decision: a broken AI call should fall back to the dashboard's static
  // renewal alert card, not a canned sentence inside this card.
  loadFailed = false;
  feedbackGiven: 'up' | 'down' | null = null;
  scenario: MyPlanStoryScenario = 'none';
  // Built from the SAME factory function the standalone renewal alert card
  // uses (DataService.getRenewalBillingAlert), so the title/button copy here
  // can never drift from that banner's wording.
  alertCardContent: IAlertCardContent | null = null;
  private loadedSignature: string | null = null;
  // Cancels a still-in-flight getPlanStory call when a new one starts (e.g.
  // the member switches contracts before the previous request settles), so a
  // stale response can never land after a newer one and overwrite the story.
  private cancelPreviousLoad$ = new Subject<void>();

  constructor(
    private myPlanStoryService: MyPlanStoryService,
    private dataService: DataService,
    private ga4Service: Ga4Service,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.contractData?.baseContract?.contractNumber || !this.jobsDataLoaded) {
      this.cancelPreviousLoad$.next();
      this.story = null;
      this.isLoading = false;
      this.loadedSignature = null;
      this.setLoadFailed(false);
      return;
    }
    // Reuses DataService's single source of truth for renewal-banner eligibility
    // (client type, contract status, expiration window, auto-renew, etc.) so the
    // CTA shown here never drifts from the standalone renewal alert card's logic.
    const showRenewalCta = this.dataService.showRealEstateRenewalBanner();
    // showPaymentMethodAlert is irrelevant to the resolver's outcome here: it only
    // ever distinguishes 'payment' from 'none', and PHASE 1 collapses both to
    // 'none' below regardless — so it's omitted rather than threaded through as
    // an unused input.
    const resolvedScenario = resolveMyPlanStoryScenario(this.showComeBackAlert, false, showRenewalCta);
    // PHASE 1: only the renewal scenario gets the AI MyPlanStory card — comeback
    // and payment already have their own standalone toast/alert elsewhere on the
    // dashboard, so this component renders nothing for them (still using the
    // resolver's precedence, so a comeback-eligible member correctly sees
    // neither). Revisit this gate when a later phase re-enables those prompts.
    this.scenario = resolvedScenario === 'renewal' ? 'renewal' : 'none';
    this.alertCardContent = this.scenario === 'renewal'
      ? this.dataService.getRenewalBillingAlert(this.pageSource)
      : null;

    if (this.scenario === 'none') {
      // Initial-phase rule: don't show an AI narrative to everyone — only to
      // members currently matching one of the measured scenarios.
      this.story = null;
      this.loadedSignature = null;
      this.setLoadFailed(false);
      return;
    }

    // Re-fetch only when the contract, the matched scenario, or any job's
    // identity/status/completion changes — jobsDataLoaded guarantees this only
    // fires once the parent's Jobs API call has actually resolved for this
    // contract. Status is part of the key because cancelling an SR changes a
    // job's status without changing the job count, and the narrative must
    // reflect that (cancelled jobs are excluded from the story's claims).
    const signature = `${this.contractData.baseContract.contractNumber}:${this.scenario}:${this.getJobsSignature()}`;
    if (signature === this.loadedSignature) {
      return;
    }
    this.loadedSignature = signature;
    this.loadStory(this.contractData, this.jobsData, this.scenario);
  }

  ngOnDestroy(): void {
    this.cancelPreviousLoad$.next();
    this.cancelPreviousLoad$.complete();
  }

  private loadStory(contractData: IContractData, jobsData: IApiClaimsHistory[], scenario: MyPlanStoryScenario): void {
    // Cancel whatever the previous call was doing before starting a new one,
    // so a slow, stale response can't land after this fresher request.
    this.cancelPreviousLoad$.next();
    this.isLoading = true;
    this.feedbackGiven = null;
    this.setLoadFailed(false);

    this.myPlanStoryService.getPlanStory(contractData, jobsData, scenario, null)
      .pipe(takeUntil(this.cancelPreviousLoad$))
      .subscribe({
        next: story => {
          this.story = story;
          this.isLoading = false;
          this.ga4Service.messageEvent(ERR_EVENT, getReRenewalAiMessageEvent(story.itemCount), this.pageSource);
        },
        // Deliberately no fallback narration here — a failed call hides this
        // card entirely (via loadFailed) so the dashboard's static renewal
        // alert card shows instead, rather than a canned sentence.
        error: (error) => {
          console.error('Error loading my plan story:', error);
          this.isLoading = false;
          this.story = null;
          this.setLoadFailed(true);
        },
      });
  }

  private getJobsSignature(): string {
    return this.jobsData
      .map(job => `${job.serviceOrderId}|${job.serviceJobStatus}|${job.completionDate ?? ''}`)
      .join(',');
  }

  private setLoadFailed(failed: boolean): void {
    this.loadFailed = failed;
    this.storyFailed.emit(failed);
  }

  submitFeedback(reaction: 'up' | 'down'): void {
    if (this.feedbackGiven || !this.story) {
      return;
    }
    this.feedbackGiven = reaction;
    this.ga4Service.myPlanStoryFeedbackEvent(reaction, this.story.source, this.scenario, MY_PLAN_STORY_LOCATION);
  }

  // Mirrors AlertCardComponent.navigate()'s external-link branch (same button-name
  // constant, same window.open behavior) so clicks from this card are directly
  // comparable to the standalone renewal alert card's click-through rate — only
  // button_location this.pageSource differs, to attribute them separately.
  // PHASE 1: alertCardContent only ever comes from getRenewalBillingAlert, whose
  // button.isExternal is always true — there is no internal-navigation case yet.
  onCtaClick(): void {
    const link = this.alertCardContent?.button?.link;
    if (!link) {
      return;
    }
    this.ga4Service.buttonClickEvent(this.getGa4PageLocation(), getReRenewalButtonEvent(this.story?.itemCount ?? 0));
    window.open(link, '_blank', 'noopener,noreferrer');
  }

  // Maps the host page's PAGE_SOURCE to its GA4 page-location identifier,
  // falling back to PAGE_SOURCE.UNKNOWN like AlertCardComponent.navigate().
  private getGa4PageLocation(): string {
    switch (this.pageSource) {
      case PAGE_SOURCE.DASHBOARD:
        return DASHBOARD_PAGE_LOCATION;
      case PAGE_SOURCE.MY_PLAN:
        return MY_PLANS_PAGE;
      case PAGE_SOURCE.SERVICE_REQUESTS:
        return SR_LIST_PAGE;
      default:
        return PAGE_SOURCE.UNKNOWN;
    }
  }
}
